import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { BarChart3, Trophy, Settings, Save, Loader2 } from 'lucide-react';
import { formatNrr } from '@/lib/cricket';

export function StandingsView({
  isPro,
  standings,
  standingsLoading,
  pointValues,
  setPointValues,
  savingPoints,
  handleSavePointValues,
}) {
  const navigate = useNavigate();

  if (!isPro) {
    return (
      <div className="space-y-6">
        <Card className="border-slate-200 shadow-card">
          <CardContent className="py-12 text-center">
            <BarChart3 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
              PRO FEATURE
            </h3>
            <p className="text-slate-600 mb-4">
              Live standings are available for Pro subscribers only.
            </p>
            <Button
              className="bg-[#00247D] hover:bg-[#001A5C]"
              onClick={() => navigate('/pricing')}
              data-testid="upgrade-standings-btn"
            >
              <Trophy className="w-4 h-4 mr-2" />
              Upgrade to Pro
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (standingsLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#00247D]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Point Values Configuration */}
      <Card className="border-slate-200 shadow-card" data-testid="point-values-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-bold text-slate-800 flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
            <Settings className="w-4 h-4 text-[#00247D]" />
            POINT VALUES
          </CardTitle>
          <p className="text-xs text-slate-500">Customize how standings are calculated for your sport</p>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-1">
              <Label className="text-xs font-medium text-green-700">Win</Label>
              <Input
                type="number"
                min="0"
                value={pointValues.win}
                onChange={(e) => setPointValues(p => ({ ...p, win: e.target.value }))}
                className="w-20 h-9 text-center text-sm"
                data-testid="points-win-input"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-medium text-red-600">Loss</Label>
              <Input
                type="number"
                min="0"
                value={pointValues.loss}
                onChange={(e) => setPointValues(p => ({ ...p, loss: e.target.value }))}
                className="w-20 h-9 text-center text-sm"
                data-testid="points-loss-input"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-medium text-slate-500">Draw</Label>
              <Input
                type="number"
                min="0"
                value={pointValues.draw}
                onChange={(e) => setPointValues(p => ({ ...p, draw: e.target.value }))}
                className="w-20 h-9 text-center text-sm"
                data-testid="points-draw-input"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-medium text-amber-700">Forfeit</Label>
              <Input
                type="number"
                min="0"
                value={pointValues.forfeit ?? 0}
                onChange={(e) => setPointValues(p => ({ ...p, forfeit: e.target.value }))}
                className="w-20 h-9 text-center text-sm"
                data-testid="points-forfeit-input"
              />
            </div>
            <Button
              size="sm"
              className="bg-[#00247D] hover:bg-[#001A5C] h-9"
              onClick={handleSavePointValues}
              disabled={savingPoints}
              data-testid="save-point-values-btn"
            >
              {savingPoints ? (
                <Loader2 className="w-4 h-4 animate-spin mr-1" />
              ) : (
                <Save className="w-4 h-4 mr-1" />
              )}
              Apply
            </Button>
            <div className="text-xs text-slate-400 ml-2">
              Presets:
              <button
                className="ml-2 text-[#00247D] hover:underline font-medium"
                onClick={() => setPointValues({ win: 3, loss: 0, draw: 1, forfeit: 0 })}
                data-testid="preset-soccer"
              >
                Soccer
              </button>
              <span className="mx-1">|</span>
              <button
                className="text-[#00247D] hover:underline font-medium"
                onClick={() => setPointValues({ win: 2, loss: 0, draw: 1, forfeit: 0 })}
                data-testid="preset-hockey"
              >
                Hockey
              </button>
              <span className="mx-1">|</span>
              <button
                className="text-[#00247D] hover:underline font-medium"
                onClick={() => setPointValues({ win: 2, loss: 1, draw: 0, forfeit: 0 })}
                data-testid="preset-basketball"
              >
                Basketball
              </button>
              <span className="mx-1">|</span>
              <button
                className="text-[#00247D] hover:underline font-medium"
                onClick={() => setPointValues({ win: 1, loss: 0, draw: 0, forfeit: 0 })}
                data-testid="preset-volleyball"
              >
                Volleyball
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Standings Tables */}
      {(() => {
        const labels = standings?.sport_meta?.score_labels || {};
        return standings?.grouped ? (
          <PoolStandings grouped={standings.grouped} labels={labels} />
        ) : standings?.standings ? (
          <LeagueStandings standings={standings.standings} labels={labels} />
        ) : (
          <Card className="border-slate-200 shadow-card">
            <CardContent className="py-12 text-center">
              <BarChart3 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">
                No standings data available. Enter match scores to see standings.
              </p>
            </CardContent>
          </Card>
        );
      })()}

      {/* Legend — sport-aware column definitions */}
      {standings && (() => {
        const labels = standings?.sport_meta?.score_labels || {};
        const pfShort = labels.points_for || 'GF';
        const paShort = labels.points_against || 'GA';
        const pdShort = labels.point_diff || 'GD';
        const pfFull = labels.points_for_full || 'Goals For';
        const paFull = labels.points_against_full || 'Goals Against';
        const pdFull = labels.point_diff_full || 'Goal Difference';
        return (
          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500" data-testid="standings-legend">
            <span><strong>P</strong> = Played</span>
            <span><strong>W</strong> = Wins</span>
            <span><strong>L</strong> = Losses</span>
            <span><strong>D</strong> = Draws</span>
            <span><strong>Pts</strong> = Total Points</span>
            <span><strong>{pfShort}</strong> = {pfFull}</span>
            <span><strong>{paShort}</strong> = {paFull}</span>
            <span><strong>{pdShort}</strong> = {pdFull}</span>
            {Object.values(standings?.standings || {}).some((r) => r && 'net_run_rate' in r) && (
              <span data-testid="nrr-legend"><strong>NRR</strong> = Net Run Rate (games with both teams&apos; overs entered)</span>
            )}
            <span className="inline-flex items-center gap-1">
              <span className="w-3 h-3 bg-green-100 rounded"></span>
              Playoff Position
            </span>
          </div>
        );
      })()}
    </div>
  );
}

function StandingsTable({ standings: rows, title, labels = {} }) {
  const pfShort = labels.points_for || 'GF';
  const paShort = labels.points_against || 'GA';
  const pdShort = labels.point_diff || 'GD';
  const showNrr = rows.some((r) => 'net_run_rate' in r);
  return (
    <Card className="border-slate-200 shadow-card overflow-hidden">
      <CardHeader className="bg-[#00247D] text-white py-3">
        <CardTitle className="text-lg font-bold" style={{ fontFamily: 'Barlow Condensed' }}>
          {title}
        </CardTitle>
      </CardHeader>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">#</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Team</th>
              <th className="px-4 py-3 text-center text-sm font-semibold text-slate-600">P</th>
              <th className="px-4 py-3 text-center text-sm font-semibold text-slate-600">W</th>
              <th className="px-4 py-3 text-center text-sm font-semibold text-slate-600">L</th>
              <th className="px-4 py-3 text-center text-sm font-semibold text-slate-600">D</th>
              <th className="px-4 py-3 text-center text-sm font-semibold text-[#00247D]">Pts</th>
              <th className="px-4 py-3 text-center text-sm font-semibold text-slate-600" data-testid="col-pf">{pfShort}</th>
              <th className="px-4 py-3 text-center text-sm font-semibold text-slate-600" data-testid="col-pa">{paShort}</th>
              <th className="px-4 py-3 text-center text-sm font-semibold text-slate-600" data-testid="col-pd">{pdShort}</th>
              {showNrr && <th className="px-4 py-3 text-center text-sm font-semibold text-slate-600" data-testid="col-nrr">NRR</th>}
              <th className="px-4 py-3 text-center text-sm font-semibold text-slate-600">Win%</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((team, idx) => (
              <tr key={team.team_id} className={`border-t border-slate-100 ${idx < 2 ? 'bg-green-50' : ''}`}>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-sm font-bold ${
                    idx === 0 ? 'bg-amber-400 text-white' : 
                    idx === 1 ? 'bg-slate-300 text-slate-700' : 
                    idx === 2 ? 'bg-amber-600 text-white' : 'bg-slate-100 text-slate-600'
                  }`}>
                    {idx + 1}
                  </span>
                </td>
                <td className="px-4 py-3 font-semibold text-slate-800">{team.team_name}</td>
                <td className="px-4 py-3 text-center text-slate-600">{team.played}</td>
                <td className="px-4 py-3 text-center font-semibold text-green-600">{team.wins}</td>
                <td className="px-4 py-3 text-center text-red-600">{team.losses}</td>
                <td className="px-4 py-3 text-center text-slate-500">{team.draws}</td>
                <td className="px-4 py-3 text-center font-bold text-[#00247D]">{team.total_points ?? 0}</td>
                <td className="px-4 py-3 text-center text-slate-600">{team.points_for}</td>
                <td className="px-4 py-3 text-center text-slate-600">{team.points_against}</td>
                <td className="px-4 py-3 text-center">
                  <span className={`font-semibold ${team.point_diff > 0 ? 'text-green-600' : team.point_diff < 0 ? 'text-red-600' : 'text-slate-500'}`}>
                    {team.point_diff > 0 ? '+' : ''}{team.point_diff}
                  </span>
                </td>
                {showNrr && (
                  <td className={`px-4 py-3 text-center font-mono text-sm ${team.net_run_rate > 0 ? 'text-green-600' : team.net_run_rate < 0 ? 'text-red-600' : 'text-slate-500'}`}
                    data-testid={`nrr-${team.team_id}`}>
                    {formatNrr(team.net_run_rate)}
                  </td>
                )}
                <td className="px-4 py-3 text-center">
                  <Badge className={`${team.win_percentage >= 50 ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
                    {team.win_percentage}%
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function PoolStandings({ grouped, labels }) {
  return (
    <div className="space-y-6">
      {Object.entries(grouped).map(([poolName, poolStandings]) => (
        <StandingsTable key={poolName} standings={poolStandings} title={`${poolName.toUpperCase()} STANDINGS`} labels={labels} />
      ))}
    </div>
  );
}

function LeagueStandings({ standings, labels }) {
  return <StandingsTable standings={standings} title="LEAGUE STANDINGS" labels={labels} />;
}
